import {SerializedError} from "@reduxjs/toolkit";
import {FetchBaseQueryError} from "@reduxjs/toolkit/query";

import {setAmplitudeUserProperties, trackAmplitudeEvent} from "@pg-mono/amplitude";
import {HomeMatchMode, OfferType, PreferencesSurveySource} from "@pg-rp/common-types";
import {
    HOMEMATCH_TINDER_ACTION_BAR_CONTENT,
    HOMEMATCH_TINDER_SENT_LEAD_MESSAGE_CONTENT,
    HomematchThumbDirection,
    IPropertyDetailDTO
} from "@pg-rp/homematch-tinder";

import {ROOM_SELECTOR_OPEN_ENDED_VALUE} from "@/components/room_selector/constants/room_selector_options";
import {otherOption} from "@/constants/otherOption";
import {HOME_MATCH_TINDER_BANNER_CONTENT, HOME_MATCH_TINDER_EMPTY_STATE_CONTENT} from "@/features/home_match/constants/home_match_tinder_content";
import {
    HomeMatchFinancingPreference,
    HomeMatchPreferencesSurveyBuildingHeight
} from "@/features/home_match/constants/preferences/home_match_preferences_values";
import {ConstructionEndDateType} from "@/features/home_match/types/ConstructionEndDateType";
import {IHomeMatchPreferencesSurveyForm, IHomeMatchSurveyApiRequest, IHomeMatchUserPreferencesForm} from "@/features/home_match/types/home_match_preferences";
import {isOpenEndedRoomsBound, RoomsRangeInput} from "@/features/home_match/utils/home_match_rooms_range";
import {parseRangeBound} from "@/features/home_match/utils/parse_range_bound";
import {View} from "@/features/km_app/types/View";
import {IPropertyListItemDTO} from "@/features/properties/types/IPropertyListItemDTO";

import {AmplitudeButtonCategory} from "./get_amplitude_button_category_by_view";
import {AmplitudeLocation} from "./get_amplitude_location_by_view";
import {getAmplitudeOfferSourceProperties} from "./get_amplitude_offer_source_properties";
import {getAmplitudePropertyItem} from "./get_amplitude_property_item";
import {LeadAmplitudeTracker} from "./lead_amplitude_tracker";
import {setAmplitudePhoneVerifiedUserProperty} from "./set_amplitude_phone_verified_user_property";
import {trackAmplitudeButtonClick} from "./track_amplitude_button_click";
import {trackAmplitudeSelectItem} from "./track_amplitude_select_item";
import {trackAmplitudeViewItemList} from "./track_amplitude_view_item_list";

enum HomeMatchAmplitudeButtonName {
    CONSULTANT_CONTACT = "consultant_contact",
    FAVOURITE_PICKER_RATE = "km_favourite_picker_rate",
    GETHOME_OFFERS = "gethome_offers",
    GHF_CONTACT = "ghf_contact",
    HOMEMATCH_CONSENT = "homematch_consent",
    HM_PREFERENCES_CHANGE = "hm_preferences_change",
    RECOMMENDATIONS_CTA = "km_recommendations_cta",
    RECOMMENDATIONS_RATE = "km_recommendations_rate",
    SET_HM_PREFERENCES = "set_hm_preferences"
}

export enum HomeMatchPreferencesSurveyAmplitudeButton {
    CLOSE = "km_preferences_close",
    DISCARD = "km_preferences_discard",
    SAVE = "km_preferences_save"
}

export enum HomeMatchPreferencesWizardAmplitudeButton {
    BACK = "hm_preferences_back",
    CHANGE_PHONE = "hm_preferences_change_phone",
    CLOSE = "hm_preferences_close",
    EDIT = "hm_preferences_edit",
    RESEND = "hm_preferences_resend",
    SUBMIT = "hm_preferences_submit"
}

export const HOME_MATCH_PREFERENCES_WIZARD_EXIT_STEP = "exit";
const HOME_MATCH_PREFERENCES_WIZARD_MODAL_NAME = "hm_preferences_modal";
const HOME_MATCH_PREFERENCES_SURVEY_MODAL_NAME = "km_preferences";

enum HomeMatchAmplitudeButtonValue {
    DISLIKE = "dislike",
    LIKE = "like"
}

enum HomeMatchAmplitudeEvent {
    PREFERENCES_SAVED = "hm_preferences_saved",
    UNSUBSCRIBE_SURVEY = "hm_unsubscribe_survey"
}

enum HomeMatchAmplitudeEventPropertyKey {
    LOAN_FINANCING = "loan_financing",
    BUDGET = "budget",
    ROOMS = "rooms",
    SELECTED_REGIONS = "selected_regions",
    SELECTED_LOCATIONS = "selected_locations",
    BUTTON_CATEGORY = "button_category",
    LOCATION = "location",
    REASON = "reason",
    SERVICE_NAME = "service_name",
    STEP = "step",
    KM_AUTO_MATCH_ENABLED = "km_auto_match_enabled",
    PROPERTY_TYPE = "property_type",
    SECONDARY_MARKET = "secondary_market",
    AREA_MIN = "area_min",
    AREA_MAX = "area_max",
    FLOOR_PREFERENCE = "floor_preference",
    BUILDING_TYPE = "building_type",
    COMPLETION_DATE = "completion_date",
    ADDITIONAL_FEATURES = "additional_features",
    LOCATION_SEARCH_METHOD = "location_search_method"
}

export enum HomeMatchAmplitudeLocation {
    DASHBOARD = "km_dashboard",
    HM_PREFERENCES = "km_hm_preferences",
    PREFERENCES_AFTER_HM = "km_preferences_after_hm",
    PREFERENCES_AFTER_LOGIN = "km_preferences_after_login",
    UNSUBSCRIBE_SURVEY = "km_homematch_unsubscribe_survey"
}

export const HOME_MATCH_FAVOURITE_PICKER_LOCATION = "km_favourite_picker" as const satisfies AmplitudeLocation;

enum HomeMatchUnsubscribeSurveyReason {
    OFFERS_NOT_MATCHING = "offers_not_matching",
    NOT_INTERESTED = "not_interested",
    FOUND_PROPERTY = "found_property",
    TOO_MANY_OFFERS = "too_many_offers",
    OTHER = "other"
}

const PREFERENCES_SURVEY_SOURCE_TO_LOCATION: Partial<Record<PreferencesSurveySource, HomeMatchAmplitudeLocation>> = {
    [PreferencesSurveySource.PREFERENCES]: HomeMatchAmplitudeLocation.HM_PREFERENCES,
    [PreferencesSurveySource.HOME_MATCH]: HomeMatchAmplitudeLocation.PREFERENCES_AFTER_HM,
    [PreferencesSurveySource.ONBOARDING]: HomeMatchAmplitudeLocation.PREFERENCES_AFTER_LOGIN
};

export const getHomeMatchAmplitudeLocationByPreferencesSource = (source: PreferencesSurveySource) =>
    PREFERENCES_SURVEY_SOURCE_TO_LOCATION[source] ?? HomeMatchAmplitudeLocation.HM_PREFERENCES;

const PREFERENCES_LOCATION_TO_BUTTON_CATEGORY: Partial<Record<HomeMatchAmplitudeLocation, AmplitudeButtonCategory>> = {
    [HomeMatchAmplitudeLocation.DASHBOARD]: AmplitudeButtonCategory.KM_DASHBOARD
};

const getPreferencesSavedButtonCategory = (location: HomeMatchAmplitudeLocation) =>
    PREFERENCES_LOCATION_TO_BUTTON_CATEGORY[location] ?? AmplitudeButtonCategory.HOMEMATCH;

const UNSUBSCRIBE_SURVEY_ANSWER_TO_REASON: Record<string, HomeMatchUnsubscribeSurveyReason> = {
    "1": HomeMatchUnsubscribeSurveyReason.OFFERS_NOT_MATCHING,
    "2": HomeMatchUnsubscribeSurveyReason.NOT_INTERESTED,
    "3": HomeMatchUnsubscribeSurveyReason.FOUND_PROPERTY,
    "4": HomeMatchUnsubscribeSurveyReason.TOO_MANY_OFFERS,
    [otherOption.value]: HomeMatchUnsubscribeSurveyReason.OTHER
};

const expandRoomsList = (rooms: RoomsRangeInput): string[] => {
    const lower = parseRangeBound(rooms?.lower);
    const upper = parseRangeBound(rooms?.upper);
    const openEnded = upper == null || isOpenEndedRoomsBound(upper);

    if (lower == null && upper == null) return [];

    const from = lower ?? ROOM_SELECTOR_OPEN_ENDED_VALUE;
    const last = openEnded ? ROOM_SELECTOR_OPEN_ENDED_VALUE : (upper ?? from) + 1;
    const values: string[] = [];

    for (let i = from; i < last; i++) {
        values.push(String(i));
    }

    if (openEnded) values.push("5+");

    return values;
};

const expandRoomsRange = (rooms: RoomsRangeInput): string => expandRoomsList(rooms).join("|");

const toFloorPreference = (floors: IHomeMatchPreferencesSurveyForm["floorsPreference"]) => {
    if (floors.length === 1) return floors[0];

    return floors.length ? floors : null;
};

const WIZARD_LOAN_FINANCING: Record<HomeMatchFinancingPreference, string> = {
    [HomeMatchFinancingPreference.CASH]: "false",
    [HomeMatchFinancingPreference.CREDIT]: "true",
    [HomeMatchFinancingPreference.UNKNOWN]: "undecided"
};

const COMPLETION_DATE_SLUG: Record<ConstructionEndDateType, string> = {
    [ConstructionEndDateType.READY]: "ready_to_move",
    [ConstructionEndDateType.THREE_MONTHS]: "up_to_3_months",
    [ConstructionEndDateType.SIX_MONTHS]: "up_to_6_months",
    [ConstructionEndDateType.YEAR]: "up_to_1_year",
    [ConstructionEndDateType.TWO_YEARS]: "up_to_2_years",
    [ConstructionEndDateType.THREE_YEARS]: "up_to_3_years"
};

const ADDITIONAL_FEATURE_SLUG: Record<number, string> = {
    4: "garden",
    5: "garage",
    6: "storage_unit",
    7: "cellar",
    8: "parking_space",
    9: "internal_parking_space",
    10: "external_parking_space",
    11: "box"
};

const toSlugList = (slugs: (string | undefined)[]) => {
    const values = slugs.filter((slug): slug is string => Boolean(slug));

    return values.length ? values : null;
};

const toPropertyType = (propertyType: IHomeMatchPreferencesSurveyForm["propertyType"]) => {
    if (propertyType == null) return null;

    return propertyType === OfferType.HOUSE ? "house" : "apartment";
};

const toBuildingType = (buildingHeight: IHomeMatchPreferencesSurveyForm["buildingHeight"]) => {
    if (buildingHeight === HomeMatchPreferencesSurveyBuildingHeight.HIGH) return ["high_rise"];

    if (buildingHeight === HomeMatchPreferencesSurveyBuildingHeight.LOW) return ["low_rise"];

    return null;
};

const toCompletionDate = (constructionEndDate: ConstructionEndDateType | null | undefined) => {
    if (constructionEndDate == null) return null;

    const slug = COMPLETION_DATE_SLUG[constructionEndDate];

    return slug ? [slug] : null;
};

interface IPreferencesWizardSavedParams {
    /** Only known when the HomeMatch switch was saved (summary confirm); otherwise it stays NULL in the event and untouched in user properties. */
    autoMatchEnabled?: boolean;
    location?: HomeMatchAmplitudeLocation;
    regionLabels: string[];
    saved: Omit<IHomeMatchSurveyApiRequest, "source">;
    step: string;
}

interface IHomeMatchUnsubscribeSurveyParams {
    selectedAnswer?: string;
    ownAnswer?: string;
}

interface IHomeMatchButtonClickOptions {
    buttonLink?: string;
    location?: string;
    offerId?: number;
    propertyId?: number;
    value?: string;
}

interface IHomeMatchRecommendationsRateClickParams {
    direction: HomematchThumbDirection;
    offerId?: number;
    propertyId?: number;
}

const getHomeMatchRateButtonValue = (direction: HomematchThumbDirection) =>
    direction === HomematchThumbDirection.Up ? HomeMatchAmplitudeButtonValue.LIKE : HomeMatchAmplitudeButtonValue.DISLIKE;

export class HomeMatchAmplitudeTracker {
    private static trackButtonClick(buttonName: string, buttonCategory: AmplitudeButtonCategory, options?: IHomeMatchButtonClickOptions) {
        trackAmplitudeButtonClick({
            buttonCategory,
            buttonLink: options?.buttonLink,
            buttonName,
            location: options?.location,
            offerId: options?.offerId,
            propertyId: options?.propertyId,
            value: options?.value
        });
    }

    static recommendationsCtaClick() {
        this.trackButtonClick(HomeMatchAmplitudeButtonName.RECOMMENDATIONS_CTA, AmplitudeButtonCategory.HOMEMATCH, {
            value: HOME_MATCH_TINDER_BANNER_CONTENT.RECOMMENDATION_READY.CTA
        });
    }

    static recommendationsRateClick({direction, offerId, propertyId}: IHomeMatchRecommendationsRateClickParams) {
        this.trackButtonClick(HomeMatchAmplitudeButtonName.RECOMMENDATIONS_RATE, AmplitudeButtonCategory.HOMEMATCH, {
            offerId,
            propertyId,
            value: getHomeMatchRateButtonValue(direction)
        });
    }

    static favouritePickerAddToFavourites(property: IPropertyListItemDTO | IPropertyDetailDTO, index?: number) {
        trackAmplitudeEvent("add_to_favourites", {
            location: HOME_MATCH_FAVOURITE_PICKER_LOCATION,
            ...getAmplitudeOfferSourceProperties(property),
            ...getAmplitudePropertyItem({index, property})
        });
    }

    static favouritePickerRateClick({direction, offerId, propertyId}: IHomeMatchRecommendationsRateClickParams) {
        this.trackButtonClick(HomeMatchAmplitudeButtonName.FAVOURITE_PICKER_RATE, AmplitudeButtonCategory.KM_FAVOURITE_PICKER, {
            location: HOME_MATCH_FAVOURITE_PICKER_LOCATION,
            offerId,
            propertyId,
            value: getHomeMatchRateButtonValue(direction)
        });
    }

    static favouritePickerViewModal() {
        trackAmplitudeEvent("view_modal", {
            location: HOME_MATCH_FAVOURITE_PICKER_LOCATION,
            modal_name: "favourite_picker"
        });
    }

    static setPreferencesClick() {
        this.trackButtonClick(HomeMatchAmplitudeButtonName.SET_HM_PREFERENCES, AmplitudeButtonCategory.KM_SENT_OFFERS);
    }

    static tinderPreleadAccepted(property: IPropertyListItemDTO | IPropertyDetailDTO, preleadId: number, mode: HomeMatchMode) {
        LeadAmplitudeTracker.trackLeadSentForHomeMatch({
            ctaText: HOMEMATCH_TINDER_ACTION_BAR_CONTENT.ASK_BUTTON,
            mode,
            property,
            transactionId: `hm-prelead-${preleadId}`
        });
    }

    static tinderPreleadError(
        property: IPropertyListItemDTO | IPropertyDetailDTO,
        mode: HomeMatchMode,
        error: FetchBaseQueryError | SerializedError | undefined
    ) {
        LeadAmplitudeTracker.trackLeadErrorForHomeMatch({
            ctaText: HOMEMATCH_TINDER_ACTION_BAR_CONTENT.ASK_BUTTON,
            error,
            fallbackMessage: HOMEMATCH_TINDER_SENT_LEAD_MESSAGE_CONTENT.ACCEPT_ERROR,
            mode,
            property
        });
    }

    static consultantContactClick() {
        this.trackButtonClick(HomeMatchAmplitudeButtonName.CONSULTANT_CONTACT, AmplitudeButtonCategory.KM_SENT_OFFERS);
    }

    static consentClick(checked: boolean) {
        this.trackButtonClick(HomeMatchAmplitudeButtonName.HOMEMATCH_CONSENT, AmplitudeButtonCategory.KM_SETTINGS, {
            value: String(checked)
        });
    }

    static emptyStatePreferencesChangeClick() {
        this.trackButtonClick(HomeMatchAmplitudeButtonName.HM_PREFERENCES_CHANGE, AmplitudeButtonCategory.KM_SENT_OFFERS, {
            value: HOME_MATCH_TINDER_EMPTY_STATE_CONTENT.PREFERENCES.CTA
        });
    }

    static emptyStateGetHomeOffersClick(buttonLink: string) {
        this.trackButtonClick(HomeMatchAmplitudeButtonName.GETHOME_OFFERS, AmplitudeButtonCategory.KM_SENT_OFFERS, {buttonLink});
    }

    static emptyStateGhfContactClick(buttonLink: string) {
        this.trackButtonClick(HomeMatchAmplitudeButtonName.GHF_CONTACT, AmplitudeButtonCategory.KM_SENT_OFFERS, {buttonLink});
    }

    static preferencesWizardView(step: string, location: HomeMatchAmplitudeLocation = HomeMatchAmplitudeLocation.DASHBOARD) {
        trackAmplitudeEvent("view_modal", {
            button_category: getPreferencesSavedButtonCategory(location),
            location,
            modal_name: HOME_MATCH_PREFERENCES_WIZARD_MODAL_NAME,
            step
        });
    }

    static preferencesWizardClick(
        buttonName: HomeMatchPreferencesWizardAmplitudeButton,
        step: string,
        location: HomeMatchAmplitudeLocation = HomeMatchAmplitudeLocation.DASHBOARD
    ) {
        this.trackButtonClick(buttonName, getPreferencesSavedButtonCategory(location), {location, value: step});
    }

    // Always the whole current set of preferences, not only the fields of the saved step; missing values are NULL.
    static preferencesWizardSaved(params: IPreferencesWizardSavedParams) {
        const {autoMatchEnabled, location = HomeMatchAmplitudeLocation.DASHBOARD, regionLabels, saved, step} = params;
        const selectedLocations = regionLabels.filter(Boolean);
        const rooms = expandRoomsList(saved.rooms);
        const completionDate = toCompletionDate(saved.construction_end_date);
        const preferences = {
            [HomeMatchAmplitudeEventPropertyKey.BUDGET]: saved.price?.upper || null,
            [HomeMatchAmplitudeEventPropertyKey.LOAN_FINANCING]: (saved.financing_preference && WIZARD_LOAN_FINANCING[saved.financing_preference]) || null,
            [HomeMatchAmplitudeEventPropertyKey.ROOMS]: rooms.length ? rooms : null,
            [HomeMatchAmplitudeEventPropertyKey.SELECTED_LOCATIONS]: selectedLocations.length ? selectedLocations : null
        };

        trackAmplitudeEvent(HomeMatchAmplitudeEvent.PREFERENCES_SAVED, {
            ...preferences,
            [HomeMatchAmplitudeEventPropertyKey.ADDITIONAL_FEATURES]: null,
            [HomeMatchAmplitudeEventPropertyKey.AREA_MAX]: null,
            [HomeMatchAmplitudeEventPropertyKey.AREA_MIN]: null,
            [HomeMatchAmplitudeEventPropertyKey.BUILDING_TYPE]: null,
            [HomeMatchAmplitudeEventPropertyKey.BUTTON_CATEGORY]: getPreferencesSavedButtonCategory(location),
            [HomeMatchAmplitudeEventPropertyKey.COMPLETION_DATE]: completionDate,
            [HomeMatchAmplitudeEventPropertyKey.FLOOR_PREFERENCE]: null,
            [HomeMatchAmplitudeEventPropertyKey.KM_AUTO_MATCH_ENABLED]: autoMatchEnabled ?? null,
            [HomeMatchAmplitudeEventPropertyKey.LOCATION]: location,
            [HomeMatchAmplitudeEventPropertyKey.PROPERTY_TYPE]: null,
            [HomeMatchAmplitudeEventPropertyKey.SECONDARY_MARKET]: null,
            [HomeMatchAmplitudeEventPropertyKey.STEP]: step
        });
        // A null user property is unset; the switch is skipped when unknown so a later save does not erase the saved choice.
        setAmplitudeUserProperties({
            ...preferences,
            ...("construction_end_date" in saved ? {[HomeMatchAmplitudeEventPropertyKey.COMPLETION_DATE]: completionDate} : {}),
            ...(autoMatchEnabled != null && {[HomeMatchAmplitudeEventPropertyKey.KM_AUTO_MATCH_ENABLED]: autoMatchEnabled})
        });
    }

    static preferencesWizardPhoneVerified(verified: boolean, location: HomeMatchAmplitudeLocation = HomeMatchAmplitudeLocation.DASHBOARD) {
        trackAmplitudeEvent("phone_verified", {
            button_category: getPreferencesSavedButtonCategory(location),
            location,
            phone_verified: verified
        });
        setAmplitudePhoneVerifiedUserProperty(verified);
    }

    static preferencesSurveyView(location: string | null) {
        trackAmplitudeEvent("view_modal", {
            location,
            modal_name: HOME_MATCH_PREFERENCES_SURVEY_MODAL_NAME
        });
    }

    static preferencesSurveyClick(buttonName: HomeMatchPreferencesSurveyAmplitudeButton, buttonCategory: AmplitudeButtonCategory, location: string | null) {
        this.trackButtonClick(buttonName, buttonCategory, {location: location ?? undefined});
    }

    // A preferences entry reports the current KM screen. Tinder and onboarding keep their entry locations.
    static preferencesSurveySaved(params: {
        form: IHomeMatchPreferencesSurveyForm;
        saved: Pick<IHomeMatchSurveyApiRequest, "area" | "construction_end_date" | "financing_preference" | "price" | "rooms">;
        screen?: {location: AmplitudeLocation} | null;
        source: PreferencesSurveySource;
    }) {
        const {form, saved, screen, source} = params;
        const entryLocation = getHomeMatchAmplitudeLocationByPreferencesSource(source);
        const reportsScreen = source === PreferencesSurveySource.PREFERENCES && screen;
        const location = reportsScreen ? reportsScreen.location : entryLocation;
        const rooms = expandRoomsList(saved.rooms ?? null);
        const selectedLocations = form.regions.map((region) => region.label).filter(Boolean);
        const preferences = {
            [HomeMatchAmplitudeEventPropertyKey.ADDITIONAL_FEATURES]: toSlugList(form.additionalExpectations.map((id) => ADDITIONAL_FEATURE_SLUG[id])),
            [HomeMatchAmplitudeEventPropertyKey.AREA_MAX]: parseRangeBound(saved.area?.upper) ?? null,
            [HomeMatchAmplitudeEventPropertyKey.AREA_MIN]: parseRangeBound(saved.area?.lower) ?? null,
            [HomeMatchAmplitudeEventPropertyKey.BUDGET]: saved.price?.upper || null,
            [HomeMatchAmplitudeEventPropertyKey.BUILDING_TYPE]: toBuildingType(form.buildingHeight),
            [HomeMatchAmplitudeEventPropertyKey.COMPLETION_DATE]: toCompletionDate(saved.construction_end_date),
            [HomeMatchAmplitudeEventPropertyKey.FLOOR_PREFERENCE]: toFloorPreference(form.floorsPreference),
            [HomeMatchAmplitudeEventPropertyKey.LOAN_FINANCING]: (saved.financing_preference && WIZARD_LOAN_FINANCING[saved.financing_preference]) || null,
            [HomeMatchAmplitudeEventPropertyKey.PROPERTY_TYPE]: toPropertyType(form.propertyType),
            [HomeMatchAmplitudeEventPropertyKey.ROOMS]: rooms.length ? rooms : null,
            [HomeMatchAmplitudeEventPropertyKey.SECONDARY_MARKET]: form.secondaryMarket,
            [HomeMatchAmplitudeEventPropertyKey.SELECTED_LOCATIONS]: selectedLocations.length ? selectedLocations : null
        };

        trackAmplitudeEvent(HomeMatchAmplitudeEvent.PREFERENCES_SAVED, {
            ...preferences,
            [HomeMatchAmplitudeEventPropertyKey.BUTTON_CATEGORY]: AmplitudeButtonCategory.HOMEMATCH,
            [HomeMatchAmplitudeEventPropertyKey.LOCATION]: location,
            [HomeMatchAmplitudeEventPropertyKey.LOCATION_SEARCH_METHOD]: form.locationSearchMethod ?? null
        });
        setAmplitudeUserProperties(preferences);
    }

    static preferencesSaved(formData: IHomeMatchUserPreferencesForm, source: PreferencesSurveySource, locationOverride?: HomeMatchAmplitudeLocation) {
        if (!formData.regions.length) return;

        const location = locationOverride ?? PREFERENCES_SURVEY_SOURCE_TO_LOCATION[source];

        if (!location) return;

        trackAmplitudeEvent(HomeMatchAmplitudeEvent.PREFERENCES_SAVED, {
            [HomeMatchAmplitudeEventPropertyKey.LOAN_FINANCING]: formData.financing_services ? "True" : "False",
            [HomeMatchAmplitudeEventPropertyKey.BUDGET]: formData.price?.upper ?? 0,
            [HomeMatchAmplitudeEventPropertyKey.ROOMS]: expandRoomsRange(formData.rooms),
            [HomeMatchAmplitudeEventPropertyKey.SELECTED_REGIONS]: formData.regions.map((region) => region.label).join("|"),
            [HomeMatchAmplitudeEventPropertyKey.BUTTON_CATEGORY]: getPreferencesSavedButtonCategory(location),
            [HomeMatchAmplitudeEventPropertyKey.LOCATION]: location
        });
    }

    static unsubscribeSurvey({selectedAnswer, ownAnswer}: IHomeMatchUnsubscribeSurveyParams) {
        const selectedReason = selectedAnswer ? UNSUBSCRIBE_SURVEY_ANSWER_TO_REASON[selectedAnswer] : undefined;

        if (!selectedReason) return;

        const normalizedOwnAnswer = ownAnswer?.trim();
        const reason = selectedReason === HomeMatchUnsubscribeSurveyReason.OTHER && normalizedOwnAnswer ? `other - ${normalizedOwnAnswer}` : selectedReason;

        trackAmplitudeEvent(HomeMatchAmplitudeEvent.UNSUBSCRIBE_SURVEY, {
            [HomeMatchAmplitudeEventPropertyKey.REASON]: reason,
            [HomeMatchAmplitudeEventPropertyKey.SERVICE_NAME]: "homematch",
            [HomeMatchAmplitudeEventPropertyKey.BUTTON_CATEGORY]: AmplitudeButtonCategory.HOMEMATCH,
            [HomeMatchAmplitudeEventPropertyKey.LOCATION]: HomeMatchAmplitudeLocation.UNSUBSCRIBE_SURVEY
        });
    }

    static viewDismissedItemList(properties: IPropertyListItemDTO[]) {
        trackAmplitudeViewItemList({properties, viewType: View.DISMISSED});
    }

    static selectItem(property: IPropertyListItemDTO, itemListName: string, index?: number) {
        trackAmplitudeSelectItem({
            property,
            itemListName,
            index
        });
    }
}
import {SerializedError} from "@reduxjs/toolkit";
import {FetchBaseQueryError} from "@reduxjs/toolkit/query";

import {setAmplitudeUserProperties, trackAmplitudeEvent} from "@pg-mono/amplitude";
import {HomeMatchMode, OfferType, PreferencesSurveySource} from "@pg-rp/common-types";
import {
    HOMEMATCH_TINDER_ACTION_BAR_CONTENT,
    HOMEMATCH_TINDER_SENT_LEAD_MESSAGE_CONTENT,
    HomematchThumbDirection,
    IPropertyDetailDTO
} from "@pg-rp/homematch-tinder";

import {ROOM_SELECTOR_OPEN_ENDED_VALUE} from "@/components/room_selector/constants/room_selector_options";
import {otherOption} from "@/constants/otherOption";
import {HOME_MATCH_TINDER_BANNER_CONTENT, HOME_MATCH_TINDER_EMPTY_STATE_CONTENT} from "@/features/home_match/constants/home_match_tinder_content";
import {
    HomeMatchFinancingPreference,
    HomeMatchPreferencesSurveyBuildingHeight
} from "@/features/home_match/constants/preferences/home_match_preferences_values";
import {ConstructionEndDateType} from "@/features/home_match/types/ConstructionEndDateType";
import {IHomeMatchPreferencesSurveyForm, IHomeMatchSurveyApiRequest, IHomeMatchUserPreferencesForm} from "@/features/home_match/types/home_match_preferences";
import {isOpenEndedRoomsBound, RoomsRangeInput} from "@/features/home_match/utils/home_match_rooms_range";
import {parseRangeBound} from "@/features/home_match/utils/parse_range_bound";
import {View} from "@/features/km_app/types/View";
import {IPropertyListItemDTO} from "@/features/properties/types/IPropertyListItemDTO";

import {AmplitudeButtonCategory} from "./get_amplitude_button_category_by_view";
import {AmplitudeLocation} from "./get_amplitude_location_by_view";
import {getAmplitudeOfferSourceProperties} from "./get_amplitude_offer_source_properties";
import {getAmplitudePropertyItem} from "./get_amplitude_property_item";
import {LeadAmplitudeTracker} from "./lead_amplitude_tracker";
import {setAmplitudePhoneVerifiedUserProperty} from "./set_amplitude_phone_verified_user_property";
import {trackAmplitudeButtonClick} from "./track_amplitude_button_click";
import {trackAmplitudeSelectItem} from "./track_amplitude_select_item";
import {trackAmplitudeViewItemList} from "./track_amplitude_view_item_list";

enum HomeMatchAmplitudeButtonName {
    CONSULTANT_CONTACT = "consultant_contact",
    FAVOURITE_PICKER_RATE = "km_favourite_picker_rate",
    GETHOME_OFFERS = "gethome_offers",
    GHF_CONTACT = "ghf_contact",
    HOMEMATCH_CONSENT = "homematch_consent",
    HM_PREFERENCES_CHANGE = "hm_preferences_change",
    RECOMMENDATIONS_CTA = "km_recommendations_cta",
    RECOMMENDATIONS_RATE = "km_recommendations_rate",
    SET_HM_PREFERENCES = "set_hm_preferences"
}

export enum HomeMatchPreferencesSurveyAmplitudeButton {
    CLOSE = "km_preferences_close",
    DISCARD = "km_preferences_discard",
    SAVE = "km_preferences_save"
}

export enum HomeMatchPreferencesWizardAmplitudeButton {
    BACK = "hm_preferences_back",
    CHANGE_PHONE = "hm_preferences_change_phone",
    CLOSE = "hm_preferences_close",
    EDIT = "hm_preferences_edit",
    RESEND = "hm_preferences_resend",
    SUBMIT = "hm_preferences_submit"
}

export const HOME_MATCH_PREFERENCES_WIZARD_EXIT_STEP = "exit";
const HOME_MATCH_PREFERENCES_WIZARD_MODAL_NAME = "hm_preferences_modal";
const HOME_MATCH_PREFERENCES_SURVEY_MODAL_NAME = "km_preferences";

enum HomeMatchAmplitudeButtonValue {
    DISLIKE = "dislike",
    LIKE = "like"
}

enum HomeMatchAmplitudeEvent {
    PREFERENCES_SAVED = "hm_preferences_saved",
    UNSUBSCRIBE_SURVEY = "hm_unsubscribe_survey"
}

enum HomeMatchAmplitudeEventPropertyKey {
    LOAN_FINANCING = "loan_financing",
    BUDGET = "budget",
    ROOMS = "rooms",
    SELECTED_REGIONS = "selected_regions",
    SELECTED_LOCATIONS = "selected_locations",
    BUTTON_CATEGORY = "button_category",
    LOCATION = "location",
    REASON = "reason",
    SERVICE_NAME = "service_name",
    STEP = "step",
    KM_AUTO_MATCH_ENABLED = "km_auto_match_enabled",
    PROPERTY_TYPE = "property_type",
    SECONDARY_MARKET = "secondary_market",
    AREA_MIN = "area_min",
    AREA_MAX = "area_max",
    FLOOR_PREFERENCE = "floor_preference",
    BUILDING_TYPE = "building_type",
    COMPLETION_DATE = "completion_date",
    ADDITIONAL_FEATURES = "additional_features",
    LOCATION_SEARCH_METHOD = "location_search_method"
}

export enum HomeMatchAmplitudeLocation {
    DASHBOARD = "km_dashboard",
    HM_PREFERENCES = "km_hm_preferences",
    PREFERENCES_AFTER_HM = "km_preferences_after_hm",
    PREFERENCES_AFTER_LOGIN = "km_preferences_after_login",
    UNSUBSCRIBE_SURVEY = "km_homematch_unsubscribe_survey"
}

export const HOME_MATCH_FAVOURITE_PICKER_LOCATION = "km_favourite_picker" as const satisfies AmplitudeLocation;

enum HomeMatchUnsubscribeSurveyReason {
    OFFERS_NOT_MATCHING = "offers_not_matching",
    NOT_INTERESTED = "not_interested",
    FOUND_PROPERTY = "found_property",
    TOO_MANY_OFFERS = "too_many_offers",
    OTHER = "other"
}

const PREFERENCES_SURVEY_SOURCE_TO_LOCATION: Partial<Record<PreferencesSurveySource, HomeMatchAmplitudeLocation>> = {
    [PreferencesSurveySource.PREFERENCES]: HomeMatchAmplitudeLocation.HM_PREFERENCES,
    [PreferencesSurveySource.HOME_MATCH]: HomeMatchAmplitudeLocation.PREFERENCES_AFTER_HM,
    [PreferencesSurveySource.ONBOARDING]: HomeMatchAmplitudeLocation.PREFERENCES_AFTER_LOGIN
};

export const getHomeMatchAmplitudeLocationByPreferencesSource = (source: PreferencesSurveySource) =>
    PREFERENCES_SURVEY_SOURCE_TO_LOCATION[source] ?? HomeMatchAmplitudeLocation.HM_PREFERENCES;

const PREFERENCES_LOCATION_TO_BUTTON_CATEGORY: Partial<Record<HomeMatchAmplitudeLocation, AmplitudeButtonCategory>> = {
    [HomeMatchAmplitudeLocation.DASHBOARD]: AmplitudeButtonCategory.KM_DASHBOARD
};

const getPreferencesSavedButtonCategory = (location: HomeMatchAmplitudeLocation) =>
    PREFERENCES_LOCATION_TO_BUTTON_CATEGORY[location] ?? AmplitudeButtonCategory.HOMEMATCH;

const UNSUBSCRIBE_SURVEY_ANSWER_TO_REASON: Record<string, HomeMatchUnsubscribeSurveyReason> = {
    "1": HomeMatchUnsubscribeSurveyReason.OFFERS_NOT_MATCHING,
    "2": HomeMatchUnsubscribeSurveyReason.NOT_INTERESTED,
    "3": HomeMatchUnsubscribeSurveyReason.FOUND_PROPERTY,
    "4": HomeMatchUnsubscribeSurveyReason.TOO_MANY_OFFERS,
    [otherOption.value]: HomeMatchUnsubscribeSurveyReason.OTHER
};

const expandRoomsList = (rooms: RoomsRangeInput): string[] => {
    const lower = parseRangeBound(rooms?.lower);
    const upper = parseRangeBound(rooms?.upper);
    const openEnded = upper == null || isOpenEndedRoomsBound(upper);

    if (lower == null && upper == null) return [];

    const from = lower ?? ROOM_SELECTOR_OPEN_ENDED_VALUE;
    const last = openEnded ? ROOM_SELECTOR_OPEN_ENDED_VALUE : (upper ?? from) + 1;
    const values: string[] = [];

    for (let i = from; i < last; i++) {
        values.push(String(i));
    }

    if (openEnded) values.push("5+");

    return values;
};

const expandRoomsRange = (rooms: RoomsRangeInput): string => expandRoomsList(rooms).join("|");

const toFloorPreference = (floors: IHomeMatchPreferencesSurveyForm["floorsPreference"]) => {
    if (floors.length === 1) return floors[0];

    return floors.length ? floors : null;
};

const WIZARD_LOAN_FINANCING: Record<HomeMatchFinancingPreference, string> = {
    [HomeMatchFinancingPreference.CASH]: "false",
    [HomeMatchFinancingPreference.CREDIT]: "true",
    [HomeMatchFinancingPreference.UNKNOWN]: "undecided"
};

const COMPLETION_DATE_SLUG: Record<ConstructionEndDateType, string> = {
    [ConstructionEndDateType.READY]: "ready_to_move",
    [ConstructionEndDateType.THREE_MONTHS]: "up_to_3_months",
    [ConstructionEndDateType.SIX_MONTHS]: "up_to_6_months",
    [ConstructionEndDateType.YEAR]: "up_to_1_year",
    [ConstructionEndDateType.TWO_YEARS]: "up_to_2_years",
    [ConstructionEndDateType.THREE_YEARS]: "up_to_3_years"
};

const ADDITIONAL_FEATURE_SLUG: Record<number, string> = {
    4: "garden",
    5: "garage",
    6: "storage_unit",
    7: "cellar",
    8: "parking_space",
    9: "internal_parking_space",
    10: "external_parking_space",
    11: "box"
};

const toSlugList = (slugs: (string | undefined)[]) => {
    const values = slugs.filter((slug): slug is string => Boolean(slug));

    return values.length ? values : null;
};

const toPropertyType = (propertyType: IHomeMatchPreferencesSurveyForm["propertyType"]) => {
    if (propertyType == null) return null;

    return propertyType === OfferType.HOUSE ? "house" : "apartment";
};

const toBuildingType = (buildingHeight: IHomeMatchPreferencesSurveyForm["buildingHeight"]) => {
    if (buildingHeight === HomeMatchPreferencesSurveyBuildingHeight.HIGH) return ["high_rise"];

    if (buildingHeight === HomeMatchPreferencesSurveyBuildingHeight.LOW) return ["low_rise"];

    return null;
};

const toCompletionDate = (constructionEndDate: ConstructionEndDateType | null | undefined) => {
    if (constructionEndDate == null) return null;

    const slug = COMPLETION_DATE_SLUG[constructionEndDate];

    return slug ? [slug] : null;
};

interface IPreferencesWizardSavedParams {
    /** Only known when the HomeMatch switch was saved (summary confirm); otherwise it stays NULL in the event and untouched in user properties. */
    autoMatchEnabled?: boolean;
    location?: HomeMatchAmplitudeLocation;
    regionLabels: string[];
    saved: Omit<IHomeMatchSurveyApiRequest, "source">;
    step: string;
}

interface IHomeMatchUnsubscribeSurveyParams {
    selectedAnswer?: string;
    ownAnswer?: string;
}

interface IHomeMatchButtonClickOptions {
    buttonLink?: string;
    location?: string;
    offerId?: number;
    propertyId?: number;
    value?: string;
}

interface IHomeMatchRecommendationsRateClickParams {
    direction: HomematchThumbDirection;
    offerId?: number;
    propertyId?: number;
}

const getHomeMatchRateButtonValue = (direction: HomematchThumbDirection) =>
    direction === HomematchThumbDirection.Up ? HomeMatchAmplitudeButtonValue.LIKE : HomeMatchAmplitudeButtonValue.DISLIKE;

export class HomeMatchAmplitudeTracker {
    private static trackButtonClick(buttonName: string, buttonCategory: AmplitudeButtonCategory, options?: IHomeMatchButtonClickOptions) {
        trackAmplitudeButtonClick({
            buttonCategory,
            buttonLink: options?.buttonLink,
            buttonName,
            location: options?.location,
            offerId: options?.offerId,
            propertyId: options?.propertyId,
            value: options?.value
        });
    }

    static recommendationsCtaClick() {
        this.trackButtonClick(HomeMatchAmplitudeButtonName.RECOMMENDATIONS_CTA, AmplitudeButtonCategory.HOMEMATCH, {
            value: HOME_MATCH_TINDER_BANNER_CONTENT.RECOMMENDATION_READY.CTA
        });
    }

    static recommendationsRateClick({direction, offerId, propertyId}: IHomeMatchRecommendationsRateClickParams) {
        this.trackButtonClick(HomeMatchAmplitudeButtonName.RECOMMENDATIONS_RATE, AmplitudeButtonCategory.HOMEMATCH, {
            offerId,
            propertyId,
            value: getHomeMatchRateButtonValue(direction)
        });
    }

    static favouritePickerAddToFavourites(property: IPropertyListItemDTO | IPropertyDetailDTO, index?: number) {
        trackAmplitudeEvent("add_to_favourites", {
            location: HOME_MATCH_FAVOURITE_PICKER_LOCATION,
            ...getAmplitudeOfferSourceProperties(property),
            ...getAmplitudePropertyItem({index, property})
        });
    }

    static favouritePickerRateClick({direction, offerId, propertyId}: IHomeMatchRecommendationsRateClickParams) {
        this.trackButtonClick(HomeMatchAmplitudeButtonName.FAVOURITE_PICKER_RATE, AmplitudeButtonCategory.KM_FAVOURITE_PICKER, {
            location: HOME_MATCH_FAVOURITE_PICKER_LOCATION,
            offerId,
            propertyId,
            value: getHomeMatchRateButtonValue(direction)
        });
    }

    static favouritePickerViewModal() {
        trackAmplitudeEvent("view_modal", {
            location: HOME_MATCH_FAVOURITE_PICKER_LOCATION,
            modal_name: "favourite_picker"
        });
    }

    static setPreferencesClick() {
        this.trackButtonClick(HomeMatchAmplitudeButtonName.SET_HM_PREFERENCES, AmplitudeButtonCategory.KM_SENT_OFFERS);
    }

    static tinderPreleadAccepted(property: IPropertyListItemDTO | IPropertyDetailDTO, preleadId: number, mode: HomeMatchMode) {
        LeadAmplitudeTracker.trackLeadSentForHomeMatch({
            ctaText: HOMEMATCH_TINDER_ACTION_BAR_CONTENT.ASK_BUTTON,
            mode,
            property,
            transactionId: `hm-prelead-${preleadId}`
        });
    }

    static tinderPreleadError(
        property: IPropertyListItemDTO | IPropertyDetailDTO,
        mode: HomeMatchMode,
        error: FetchBaseQueryError | SerializedError | undefined
    ) {
        LeadAmplitudeTracker.trackLeadErrorForHomeMatch({
            ctaText: HOMEMATCH_TINDER_ACTION_BAR_CONTENT.ASK_BUTTON,
            error,
            fallbackMessage: HOMEMATCH_TINDER_SENT_LEAD_MESSAGE_CONTENT.ACCEPT_ERROR,
            mode,
            property
        });
    }

    static consultantContactClick() {
        this.trackButtonClick(HomeMatchAmplitudeButtonName.CONSULTANT_CONTACT, AmplitudeButtonCategory.KM_SENT_OFFERS);
    }

    static consentClick(checked: boolean) {
        this.trackButtonClick(HomeMatchAmplitudeButtonName.HOMEMATCH_CONSENT, AmplitudeButtonCategory.KM_SETTINGS, {
            value: String(checked)
        });
    }

    static emptyStatePreferencesChangeClick() {
        this.trackButtonClick(HomeMatchAmplitudeButtonName.HM_PREFERENCES_CHANGE, AmplitudeButtonCategory.KM_SENT_OFFERS, {
            value: HOME_MATCH_TINDER_EMPTY_STATE_CONTENT.PREFERENCES.CTA
        });
    }

    static emptyStateGetHomeOffersClick(buttonLink: string) {
        this.trackButtonClick(HomeMatchAmplitudeButtonName.GETHOME_OFFERS, AmplitudeButtonCategory.KM_SENT_OFFERS, {buttonLink});
    }

    static emptyStateGhfContactClick(buttonLink: string) {
        this.trackButtonClick(HomeMatchAmplitudeButtonName.GHF_CONTACT, AmplitudeButtonCategory.KM_SENT_OFFERS, {buttonLink});
    }

    static preferencesWizardView(step: string, location: HomeMatchAmplitudeLocation = HomeMatchAmplitudeLocation.DASHBOARD) {
        trackAmplitudeEvent("view_modal", {
            button_category: getPreferencesSavedButtonCategory(location),
            location,
            modal_name: HOME_MATCH_PREFERENCES_WIZARD_MODAL_NAME,
            step
        });
    }

    static preferencesWizardClick(
        buttonName: HomeMatchPreferencesWizardAmplitudeButton,
        step: string,
        location: HomeMatchAmplitudeLocation = HomeMatchAmplitudeLocation.DASHBOARD
    ) {
        this.trackButtonClick(buttonName, getPreferencesSavedButtonCategory(location), {location, value: step});
    }

    // Always the whole current set of preferences, not only the fields of the saved step; missing values are NULL.
    static preferencesWizardSaved(params: IPreferencesWizardSavedParams) {
        const {autoMatchEnabled, location = HomeMatchAmplitudeLocation.DASHBOARD, regionLabels, saved, step} = params;
        const selectedLocations = regionLabels.filter(Boolean);
        const rooms = expandRoomsList(saved.rooms);
        const completionDate = toCompletionDate(saved.construction_end_date);
        const preferences = {
            [HomeMatchAmplitudeEventPropertyKey.BUDGET]: saved.price?.upper || null,
            [HomeMatchAmplitudeEventPropertyKey.LOAN_FINANCING]: (saved.financing_preference && WIZARD_LOAN_FINANCING[saved.financing_preference]) || null,
            [HomeMatchAmplitudeEventPropertyKey.ROOMS]: rooms.length ? rooms : null,
            [HomeMatchAmplitudeEventPropertyKey.SELECTED_LOCATIONS]: selectedLocations.length ? selectedLocations : null
        };

        trackAmplitudeEvent(HomeMatchAmplitudeEvent.PREFERENCES_SAVED, {
            ...preferences,
            [HomeMatchAmplitudeEventPropertyKey.ADDITIONAL_FEATURES]: null,
            [HomeMatchAmplitudeEventPropertyKey.AREA_MAX]: null,
            [HomeMatchAmplitudeEventPropertyKey.AREA_MIN]: null,
            [HomeMatchAmplitudeEventPropertyKey.BUILDING_TYPE]: null,
            [HomeMatchAmplitudeEventPropertyKey.BUTTON_CATEGORY]: getPreferencesSavedButtonCategory(location),
            [HomeMatchAmplitudeEventPropertyKey.COMPLETION_DATE]: completionDate,
            [HomeMatchAmplitudeEventPropertyKey.FLOOR_PREFERENCE]: null,
            [HomeMatchAmplitudeEventPropertyKey.KM_AUTO_MATCH_ENABLED]: autoMatchEnabled ?? null,
            [HomeMatchAmplitudeEventPropertyKey.LOCATION]: location,
            [HomeMatchAmplitudeEventPropertyKey.PROPERTY_TYPE]: null,
            [HomeMatchAmplitudeEventPropertyKey.SECONDARY_MARKET]: null,
            [HomeMatchAmplitudeEventPropertyKey.STEP]: step
        });
        // A null user property is unset; the switch is skipped when unknown so a later save does not erase the saved choice.
        setAmplitudeUserProperties({
            ...preferences,
            ...("construction_end_date" in saved ? {[HomeMatchAmplitudeEventPropertyKey.COMPLETION_DATE]: completionDate} : {}),
            ...(autoMatchEnabled != null && {[HomeMatchAmplitudeEventPropertyKey.KM_AUTO_MATCH_ENABLED]: autoMatchEnabled})
        });
    }

    static preferencesWizardPhoneVerified(verified: boolean, location: HomeMatchAmplitudeLocation = HomeMatchAmplitudeLocation.DASHBOARD) {
        trackAmplitudeEvent("phone_verified", {
            button_category: getPreferencesSavedButtonCategory(location),
            location,
            phone_verified: verified
        });
        setAmplitudePhoneVerifiedUserProperty(verified);
    }

    static preferencesSurveyView(location: string | null) {
        trackAmplitudeEvent("view_modal", {
            location,
            modal_name: HOME_MATCH_PREFERENCES_SURVEY_MODAL_NAME
        });
    }

    static preferencesSurveyClick(buttonName: HomeMatchPreferencesSurveyAmplitudeButton, buttonCategory: AmplitudeButtonCategory, location: string | null) {
        this.trackButtonClick(buttonName, buttonCategory, {location: location ?? undefined});
    }

    // A preferences entry reports the current KM screen. Tinder and onboarding keep their entry locations.
    static preferencesSurveySaved(params: {
        form: IHomeMatchPreferencesSurveyForm;
        saved: Pick<IHomeMatchSurveyApiRequest, "area" | "construction_end_date" | "financing_preference" | "price" | "rooms">;
        screen?: {location: AmplitudeLocation} | null;
        source: PreferencesSurveySource;
    }) {
        const {form, saved, screen, source} = params;
        const entryLocation = getHomeMatchAmplitudeLocationByPreferencesSource(source);
        const reportsScreen = source === PreferencesSurveySource.PREFERENCES && screen;
        const location = reportsScreen ? reportsScreen.location : entryLocation;
        const rooms = expandRoomsList(saved.rooms ?? null);
        const selectedLocations = form.regions.map((region) => region.label).filter(Boolean);
        const preferences = {
            [HomeMatchAmplitudeEventPropertyKey.ADDITIONAL_FEATURES]: toSlugList(form.additionalExpectations.map((id) => ADDITIONAL_FEATURE_SLUG[id])),
            [HomeMatchAmplitudeEventPropertyKey.AREA_MAX]: parseRangeBound(saved.area?.upper) ?? null,
            [HomeMatchAmplitudeEventPropertyKey.AREA_MIN]: parseRangeBound(saved.area?.lower) ?? null,
            [HomeMatchAmplitudeEventPropertyKey.BUDGET]: saved.price?.upper || null,
            [HomeMatchAmplitudeEventPropertyKey.BUILDING_TYPE]: toBuildingType(form.buildingHeight),
            [HomeMatchAmplitudeEventPropertyKey.COMPLETION_DATE]: toCompletionDate(saved.construction_end_date),
            [HomeMatchAmplitudeEventPropertyKey.FLOOR_PREFERENCE]: toFloorPreference(form.floorsPreference),
            [HomeMatchAmplitudeEventPropertyKey.LOAN_FINANCING]: (saved.financing_preference && WIZARD_LOAN_FINANCING[saved.financing_preference]) || null,
            [HomeMatchAmplitudeEventPropertyKey.PROPERTY_TYPE]: toPropertyType(form.propertyType),
            [HomeMatchAmplitudeEventPropertyKey.ROOMS]: rooms.length ? rooms : null,
            [HomeMatchAmplitudeEventPropertyKey.SECONDARY_MARKET]: form.secondaryMarket,
            [HomeMatchAmplitudeEventPropertyKey.SELECTED_LOCATIONS]: selectedLocations.length ? selectedLocations : null
        };

        trackAmplitudeEvent(HomeMatchAmplitudeEvent.PREFERENCES_SAVED, {
            ...preferences,
            [HomeMatchAmplitudeEventPropertyKey.BUTTON_CATEGORY]: AmplitudeButtonCategory.HOMEMATCH,
            [HomeMatchAmplitudeEventPropertyKey.LOCATION]: location,
            [HomeMatchAmplitudeEventPropertyKey.LOCATION_SEARCH_METHOD]: form.locationSearchMethod ?? null
        });
        setAmplitudeUserProperties(preferences);
    }

    static preferencesSaved(formData: IHomeMatchUserPreferencesForm, source: PreferencesSurveySource, locationOverride?: HomeMatchAmplitudeLocation) {
        if (!formData.regions.length) return;

        const location = locationOverride ?? PREFERENCES_SURVEY_SOURCE_TO_LOCATION[source];

        if (!location) return;

        trackAmplitudeEvent(HomeMatchAmplitudeEvent.PREFERENCES_SAVED, {
            [HomeMatchAmplitudeEventPropertyKey.LOAN_FINANCING]: formData.financing_services ? "True" : "False",
            [HomeMatchAmplitudeEventPropertyKey.BUDGET]: formData.price?.upper ?? 0,
            [HomeMatchAmplitudeEventPropertyKey.ROOMS]: expandRoomsRange(formData.rooms),
            [HomeMatchAmplitudeEventPropertyKey.SELECTED_REGIONS]: formData.regions.map((region) => region.label).join("|"),
            [HomeMatchAmplitudeEventPropertyKey.BUTTON_CATEGORY]: getPreferencesSavedButtonCategory(location),
            [HomeMatchAmplitudeEventPropertyKey.LOCATION]: location
        });
    }

    static unsubscribeSurvey({selectedAnswer, ownAnswer}: IHomeMatchUnsubscribeSurveyParams) {
        const selectedReason = selectedAnswer ? UNSUBSCRIBE_SURVEY_ANSWER_TO_REASON[selectedAnswer] : undefined;

        if (!selectedReason) return;

        const normalizedOwnAnswer = ownAnswer?.trim();
        const reason = selectedReason === HomeMatchUnsubscribeSurveyReason.OTHER && normalizedOwnAnswer ? `other - ${normalizedOwnAnswer}` : selectedReason;

        trackAmplitudeEvent(HomeMatchAmplitudeEvent.UNSUBSCRIBE_SURVEY, {
            [HomeMatchAmplitudeEventPropertyKey.REASON]: reason,
            [HomeMatchAmplitudeEventPropertyKey.SERVICE_NAME]: "homematch",
            [HomeMatchAmplitudeEventPropertyKey.BUTTON_CATEGORY]: AmplitudeButtonCategory.HOMEMATCH,
            [HomeMatchAmplitudeEventPropertyKey.LOCATION]: HomeMatchAmplitudeLocation.UNSUBSCRIBE_SURVEY
        });
    }

    static viewDismissedItemList(properties: IPropertyListItemDTO[]) {
        trackAmplitudeViewItemList({properties, viewType: View.DISMISSED});
    }

    static selectItem(property: IPropertyListItemDTO, itemListName: string, index?: number) {
        trackAmplitudeSelectItem({
            property,
            itemListName,
            index
        });
    }
}
